import { Film, Users, Smartphone, Heart, Package, Building, Home, Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";
import LazyVideo from "./LazyVideo";

const Services = () => {
  const navigate = useNavigate();

  const services = [
    {
      icon: Film,
      title: "Imagefilm",
      slug: "imagefilm",
      thumbnail: "https://pub-626563a90a7f431c96f87c5838c120bc.r2.dev/videos/geiger-kunedzufriedenheit-fuer-die-em-2024.mp4",
      poster: "/thumbnails/imagefilm.jpg",
      description: "Professionelle Imagefilme, die Ihre Marke und Unternehmenswerte authentisch präsentieren.",
      tags: ["Unternehmensfilm", "Markenfilm", "Corporate"],
    },
    {
      icon: Users,
      title: "Recruiting",
      slug: "recruiting",
      thumbnail: "https://pub-626563a90a7f431c96f87c5838c120bc.r2.dev/videos/weisser-und-griesshaber-gmbh-recruiting-video-2022.mp4",
      poster: "/thumbnails/recruiting.jpg",
      description: "Mitarbeiter-Videos und Recruiting-Filme, die Talente begeistern und Ihre Arbeitgebermarke stärken.",
      tags: ["Employer Branding", "Karriere", "Teamvorstellung"],
    },
    {
      icon: Smartphone,
      title: "Social Media Content",
      slug: "social-media-content",
      thumbnail: "https://pub-626563a90a7f431c96f87c5838c120bc.r2.dev/videos/tag-3-gt3-rs-berge-2.mp4",
      poster: "/thumbnails/social-media.jpg",
      description: "Reels, TikToks und YouTube Shorts – plattformgerecht und performanceorientiert für maximale Reichweite.",
      tags: ["Reels", "TikTok", "YouTube Shorts"],
    },
    {
      icon: Heart,
      title: "Hochzeitsvideo",
      slug: "hochzeitsvideo",
      thumbnail: "https://pub-626563a90a7f431c96f87c5838c120bc.r2.dev/videos/hochzeit-von-sabrina-und-christian-15-10-22.mp4",
      poster: "/thumbnails/hochzeit.jpg",
      description: "Emotionale Hochzeitsfilme, die Ihren besonderen Tag für die Ewigkeit festhalten.",
      tags: ["Hochzeit", "Event", "Emotion"],
    },
    {
      icon: Package,
      title: "Produktvideo",
      slug: "produktvideo",
      thumbnail: "https://pub-626563a90a7f431c96f87c5838c120bc.r2.dev/videos/327-spirti-21-03-25.mp4",
      poster: "/thumbnails/produkt.jpg",
      description: "Verkaufsstarke Produktfilme und E-Commerce-Content, der Ihre Produkte ins beste Licht rückt.",
      tags: ["E-Commerce", "Produktfilm", "UGC-Style"],
    },
    {
      icon: Building,
      title: "Event & City",
      slug: "event-city",
      thumbnail: "https://pub-626563a90a7f431c96f87c5838c120bc.r2.dev/videos/stadt-villingen-abendflair.mp4",
      poster: "/thumbnails/event.jpg",
      description: "Stadt-Kampagnen, Event-Coverage und Messe-Videos – Atmosphäre, die begeistert.",
      tags: ["Events", "Stadt-Marketing", "Messen"],
    },
    {
      icon: Home,
      title: "Immobilien",
      slug: "immobilien",
      thumbnail: "https://pub-626563a90a7f431c96f87c5838c120bc.r2.dev/videos/roomtour-penthouse-sigmaringen.mp4",
      poster: "/thumbnails/roomtour-penthouse.webp",
      description: "Immobilien- und Architekturvideos: Roomtours, Drohnenaufnahmen und Lifestyle-Cuts, die Objekte verkaufen.",
      tags: ["Roomtour", "Drohne", "Architektur"],
    },
    {
      icon: Sparkles,
      title: "KI-Video",
      slug: "ki-video-generation",
      thumbnail: "https://pub-626563a90a7f431c96f87c5838c120bc.r2.dev/videos/ki-commercial-elevator.mp4",
      poster: "/thumbnails/ki-commercial-elevator.webp",
      description: "KI-gestützte Videoproduktion mit Freycreate.ai: Marketing-Spots, Hintergrund-Replacements und Workflow-Beschleunigung 5×.",
      tags: ["AI Video", "Freycreate", "Workflow"],
    },
  ];

  return (
    <section id="services" className="py-24 bg-secondary/30" data-testid="services-section">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl font-bold mb-4" data-testid="services-title">
            Von Reel bis Imagefilm
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Von der ersten Idee bis zum fertigen Film – für Unternehmen, Events und private Anlässe.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((service, index) => {
            const Icon = service.icon;
            return (
              <div
                key={index}
                onClick={() => navigate(`/service/${service.slug}`)}
                className="group bg-background/50 backdrop-blur-sm border border-white/5 rounded-2xl overflow-hidden hover-tilt card-glow transition-all duration-300 cursor-pointer"
                data-testid={`service-${index}`}
              >
                {/* Video Thumbnail */}
                {service.thumbnail && (
                  <div className="aspect-video overflow-hidden relative">
                    <LazyVideo
                      src={service.thumbnail}
                      poster={service.poster}
                      hoverToPlay={true}
                      aspectRatio="16/9"
                      className="w-full h-full"
                    />
                    {/* Icon Overlay */}
                    <div className="absolute top-4 left-4 w-12 h-12 rounded-xl bg-background/80 backdrop-blur-sm flex items-center justify-center">
                      <Icon className="w-6 h-6 text-[hsl(var(--accent))]" />
                    </div>
                  </div>
                )}

                {/* Content */}
                <div className="p-8">
                  {/* Title */}
                  <h3 className="text-2xl font-bold mb-3" data-testid={`service-title-${index}`}>
                    {service.title}
                  </h3>

                  {/* Description */}
                  <p className="text-muted-foreground mb-4 leading-relaxed">
                    {service.description}
                  </p>

                  {/* Tags */}
                  <div className="flex flex-wrap gap-2">
                    {service.tags.map((tag, tagIndex) => (
                      <span
                        key={tagIndex}
                        className="px-3 py-1 text-xs font-medium bg-secondary rounded-full text-muted-foreground"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Services;
